import { ApiError, PaginationParams } from '../../types/api.types';
import { ValidationResult } from '../../types/api.types';

export abstract class BaseService<T> {
    protected abstract endpoint: string;
    protected abstract validate(data: any): ValidationResult;

    protected async request<R>(config: RequestInit): Promise<R> {
        try {
            const response = await fetch((config as any).url, {
                ...config,
                headers: {
                    'Content-Type': 'application/json',
                    ...config.headers,
                },
            });

            if (!response.ok) {
                const error: ApiError = await response.json();
                throw error;
            }

            if (response.status === 204) {
                return {} as R;
            }

            return await response.json();
        } catch (error) {
            throw this.handleError(error);
        }
    }

    protected async get<R>(params?: PaginationParams): Promise<R> {
        const queryParams = params
            ? Object.entries(params)
                .filter(([_, value]) => value !== undefined)
                .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
                .join('&')
            : '';
        return this.request<R>({
            method: 'GET',
            url: `${this.endpoint}${queryParams ? '?' + queryParams : ''}`,
        } as RequestInit);
    }

    protected async getById<R>(id: string | number): Promise<R> {
        return this.request<R>({
            method: 'GET',
            url: `${this.endpoint}/${id}`,
        } as RequestInit);
    }

    protected async create<R>(data: Partial<T>): Promise<R> {
        const validation = this.validate(data);
        if (!validation.isValid) {
            throw new Error(JSON.stringify({
                code: 'VALIDATION_ERROR',
                message: 'Validation failed',
                details: validation.errors,
            }));
        }

        return this.request<R>({
            method: 'POST',
            url: this.endpoint,
            body: JSON.stringify(data),
        } as RequestInit);
    }

    protected async update<R>(id: string | number, data: Partial<T>): Promise<R> {
        const validation = this.validate(data);
        if (!validation.isValid) {
            throw new Error(JSON.stringify({
                code: 'VALIDATION_ERROR',
                message: 'Validation failed',
                details: validation.errors,
            }));
        }

        return this.request<R>({
            method: 'PUT',
            url: `${this.endpoint}/${id}`,
            body: JSON.stringify(data),
        } as RequestInit);
    }

    protected async delete(id: string | number): Promise<void> {
        return this.request<void>({
            method: 'DELETE',
            url: `${this.endpoint}/${id}`,
        } as RequestInit);
    }

    protected handleError(error: any): ApiError {
        if (error instanceof Error) {
            return {
                code: 'UNKNOWN_ERROR',
                message: error.message,
            };
        }
        return error as ApiError;
    }
} 